import { useEffect, useMemo, useState } from "react";

import { cn } from "@/lib/utils";
import { Search } from "lucide-react";

import UsersTable from "./users-table";

import { AdminUser, useAdminUsersQuery } from "@/queries/admin/users";
import { useDebounced } from "@/utils/debouncer";

export const USERS_PER_PAGE = 10;

const SORTABLE_FIELDS = ["name"] as const;
export type SortableField = (typeof SORTABLE_FIELDS)[number];

const AdminUsers = () => {
  const [_selectedUser, setSelectedUser] = useState<AdminUser>();
  const [searchString, setSearchString] = useState("");
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortableField>("name");
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  const debouncedFilter = useDebounced(searchString, 300);

  useEffect(() => {
    setPage(1);
  }, [debouncedFilter]);

  const { adminUsersPage, adminUsersFetching } = useAdminUsersQuery({
    limit: USERS_PER_PAGE,
    offset: (page - 1) * USERS_PER_PAGE,
    filter: debouncedFilter,
    sortField,
    sortDirection: sortDirection === 1 ? "asc" : "desc",
  });

  const count = useMemo(() => adminUsersPage?.total ?? 0, [adminUsersPage]);

  return (
    <div>
      <h3 className="py-4 text-2xl font-semibold group-hover:underline text-left">
        Users
      </h3>
      <div className="w-full flex flex-col gap-4 items-start self-stretch">
        <div className="flex flex-col md:flex-row md:items-center justify-between self-stretch">
          <p className="text-lg text-[#181d27] font-semibold">
            {`${count} ${count === 1 ? "User" : "Users"}`}
          </p>
          <div className="flex flex-row items-center gap-3">
            <div
              className={cn(
                "flex flex-row items-center gap-0.5",
                "border-b border-[#57576B] transition-colors focus-within:border-black",
              )}
            >
              <Search />
              <input
                type="text"
                className={cn(
                  "flex h-10 w-full min-w-48 px-3 py-2 text-base",
                  "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
                )}
                placeholder="Search for any user"
                value={searchString}
                onChange={(ev) => {
                  ev.preventDefault();
                  setSearchString(ev.target.value);
                }}
              />
            </div>
          </div>
        </div>
        <UsersTable
          currentPageUsers={adminUsersPage?.rows}
          totalUsers={adminUsersPage?.total}
          page={page}
          pageSize={USERS_PER_PAGE}
          sortField={sortField}
          sortDirection={sortDirection}
          isLoading={adminUsersFetching}
          setPage={setPage}
          setSortField={setSortField}
          setSortDirection={setSortDirection}
          setSelectedUser={setSelectedUser}
        />
      </div>
      {/* <ApprovedCandidateDrawer
        candidate={selectedCandidate}
        setOpen={() => setSelectedCandidate(undefined)}
      /> */}
    </div>
  );
};

export default AdminUsers;
